<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Pricing</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage offering prices across currencies</p>
                                </div>
                                <button
                                        type="button"
                                        class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                        :disabled="isLoading"
                                        @click="loadData"
                                >
                                        <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Refresh
                                </button>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Filters Card -->
                        <div class="bg-white rounded-xl shadow-sm py-4 px-6 mb-4">
                                <div class="flex flex-wrap gap-6 items-center">
                                        <!-- Search -->
                                        <div class="flex-1 min-w-48">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search offerings..."
                                                                class="w-full py-2 pr-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
                                                                @input="applyFilters"
                                                        >
                                                </div>
                                        </div>

                                        <!-- Stock Status Filter -->
                                        <div class="flex items-center gap-2">
                                                <select
                                                        v-model="filters.stockStatus"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none min-w-32 focus:border-orange-7"
                                                        @change="applyFilters"
                                                >
                                                        <option value="all">All</option>
                                                        <option value="instock">In Stock</option>
                                                        <option value="outofstock">Out of Stock</option>
                                                </select>
                                        </div>

                                        <!-- Flexgroup Filter -->
                                        <div class="flex items-center gap-2">
                                                <select
                                                        v-model="filters.flexGroup"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none bg-white min-w-[10rem] focus:border-orange-7"
                                                        @change="applyFilters"
                                                >
                                                        <option value="all">All Groups</option>
                                                        <option v-for="fg in flexGroups" :key="fg.id" :value="String(fg.id)">
                                                                {{ fg.name }}
                                                        </option>
                                                </select>
                                        </div>

                                        <!-- Price Type Filter -->
                                        <div class="flex items-center gap-2">
                                                <label class="text-sm font-medium text-gray-700 whitespace-nowrap">Show Records:</label>
                                                <select
                                                        v-model="filters.priceType"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none min-w-40 focus:border-orange-7"
                                                        @change="applyFilters"
                                                >
                                                        <option value="all">All</option>
                                                        <option value="discounted">Discounted only</option>
                                                        <option value="missing">Missing prices</option>
                                                </select>
                                        </div>

                                        <!-- Stats -->
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 py-1 px-3 rounded-full font-medium">
                                                        {{ filteredPrices.length }} records
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading pricing data...</span>
                        </div>

                        <!-- Error State -->
                        <div v-else-if="error" class="bg-white rounded-xl shadow-sm p-8 text-center">
                                <svg class="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <p class="text-gray-700 font-medium mb-2">Failed to load data</p>
                                <p class="text-gray-500 text-sm mb-4">{{ error }}</p>
                                <button
                                        type="button"
                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 hover:bg-orange-600 text-white rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                        @click="loadData"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Try Again
                                </button>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto max-h-[70vh]">
                                        <table class="w-full border-collapse min-w-[900px]">
                                                <thead class="sticky top-0 z-10">
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Offering</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-16">Image</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Currency</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Base Price (Catalog)</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Base Price (Discount)</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Page Price (Catalog)</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Page Price (Discount)</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="filteredPrices.length === 0">
                                                                <td colspan="8" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No pricing records found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 m-0">Try adjusting your filters</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="price in paginatedPrices"
                                                                :key="price.id"
                                                                class="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ price.offeringid }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900">
                                                                        <div class="font-medium">{{ getOfferingName(price.offeringid) }}</div>
                                                                        <div class="text-xs text-gray-500">{{ getOfferingDescription(price.offeringid) }}</div>
                                                                </td>
                                                                <td class="py-2 px-4">
                                                                        <img
                                                                                v-if="getOfferingThumbnail(price.offeringid)"
                                                                                :src="getOfferingThumbnail(price.offeringid) ?? undefined"
                                                                                alt="Thumbnail"
                                                                                class="w-10 h-10 object-cover rounded-md border border-gray-200"
                                                                        >
                                                                        <div v-else class="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center">
                                                                                <svg class="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                                </svg>
                                                                        </div>
                                                                </td>
                                                                <td class="py-3 px-4 text-sm text-gray-900">
                                                                        <span class="font-medium">{{ getCurrencyDisplay(price.currency) }}</span>
                                                                </td>
                                                                <!-- Base Price Catalog -->
                                                                <td class="py-2 px-3 text-center">
                                                                        <price-input
                                                                                :price="price"
                                                                                field="price_base_from"
                                                                                :currency-prefix="getCurrencyPrefix(price.currency)"
                                                                                :saving-field="savingField"
                                                                                @save="savePrice"
                                                                        />
                                                                </td>
                                                                <!-- Base Price Discount -->
                                                                <td class="py-2 px-3 text-center">
                                                                        <price-input
                                                                                :price="price"
                                                                                field="price_base"
                                                                                :currency-prefix="getCurrencyPrefix(price.currency)"
                                                                                :saving-field="savingField"
                                                                                :is-discounted="price.price_base !== price.price_base_from"
                                                                                @save="savePrice"
                                                                        />
                                                                </td>
                                                                <!-- Page Price Catalog -->
                                                                <td class="py-2 px-3 text-center">
                                                                        <price-input
                                                                                :price="price"
                                                                                field="price_page_from"
                                                                                :currency-prefix="getCurrencyPrefix(price.currency)"
                                                                                :saving-field="savingField"
                                                                                @save="savePrice"
                                                                        />
                                                                </td>
                                                                <!-- Page Price Discount -->
                                                                <td class="py-2 px-3 text-center">
                                                                        <price-input
                                                                                :price="price"
                                                                                field="price_page"
                                                                                :currency-prefix="getCurrencyPrefix(price.currency)"
                                                                                :saving-field="savingField"
                                                                                :is-discounted="price.price_page !== price.price_page_from"
                                                                                @save="savePrice"
                                                                        />
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center py-4 px-6 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredPrices.length) }} of {{ filteredPrices.length }} records
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-10 transition-colors"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Toast Notification -->
                <div v-if="toastMessage" class="fixed bottom-6 right-6 z-50">
                        <div
                                class="flex items-center gap-3 py-4 px-5 rounded-lg shadow-lg"
                                :class="toastVariant === 'success' ? 'bg-emerald-600' : 'bg-red-600'"
                        >
                                <svg v-if="toastVariant === 'success'" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <svg v-else class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span class="text-sm font-medium text-white">{{ toastMessage }}</span>
                                <button
                                        type="button"
                                        class="ml-2 text-white opacity-70 bg-transparent border-none cursor-pointer p-0 hover:opacity-100 transition-opacity"
                                        @click="toastMessage = ''"
                                >
                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                </button>
                        </div>
                </div>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, type Ref, type ComputedRef, type PropType } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { usePricingData, type PriceRecord, type NormalizedOffering } from 'composables/usePricingData';
import PricingService from 'services/PricingService';

interface Filters {
        flexGroup: string;
        priceType: 'all' | 'discounted' | 'missing';
        stockStatus: 'all' | 'instock' | 'outofstock';
}

type ToastVariant = 'success' | 'error';

type PriceField = 'price_base' | 'price_base_from' | 'price_page' | 'price_page_from';

const PriceInput = defineComponent({
        name: 'PriceInput',
        props: {
                price: { type: Object as PropType<PriceRecord>, required: true },
                field: { type: String as PropType<PriceField>, required: true },
                currencyPrefix: { type: String, default: '' },
                savingField: { type: String, default: '' },
                isDiscounted: { type: Boolean, default: false },
        },
        emits: ['save'],
        setup(props, { emit }) {
                const isEditing: Ref<boolean> = ref(false);
                const editValue: Ref<string> = ref('');

                const displayValue: ComputedRef<string> = computed(() => {
                        const value = props.price[props.field];
                        if (value === null || value === undefined) return '-';
                        return (value / 100).toFixed(2);
                });

                const isSaving: ComputedRef<boolean> = computed(() => props.savingField === `${props.price.id}-${props.field}`);

                const startEdit = (): void => {
                        editValue.value = displayValue.value === '-' ? '' : displayValue.value;
                        isEditing.value = true;
                };

                const save = (): void => {
                        isEditing.value = false;
                        const newValue = editValue.value === '' ? null : Math.round(parseFloat(editValue.value) * 100);
                        if (newValue !== props.price[props.field]) {
                                emit('save', props.price.id, props.field, newValue);
                        }
                };

                const cancel = (): void => {
                        isEditing.value = false;
                };

                return {
                        isEditing,
                        editValue,
                        displayValue,
                        isSaving,
                        startEdit,
                        save,
                        cancel,
                };
        },
        template: `
                <div class="relative">
                        <div v-if="isEditing" class="flex items-center gap-1">
                                <span class="text-xs text-gray-400">{{ currencyPrefix }}</span>
                                <input
                                        v-model="editValue"
                                        type="number"
                                        step="0.01"
                                        class="w-20 py-1 px-2 border border-orange-7 rounded text-sm text-center outline-none"
                                        @blur="save"
                                        @keydown.enter="save"
                                        @keydown.escape="cancel"
                                        autofocus
                                >
                        </div>
                        <div v-else class="flex items-center justify-center gap-1">
                                <span v-if="isSaving" class="text-gray-400 text-sm">...</span>
                                <button
                                        v-else
                                        type="button"
                                        class="py-1 px-2 text-sm border-none bg-transparent cursor-pointer rounded transition-colors"
                                        :class="isDiscounted ? 'text-green-600 font-medium hover:bg-green-50' : 'text-gray-900 hover:bg-gray-100'"
                                        @click="startEdit"
                                >
                                        {{ currencyPrefix }}{{ displayValue }}
                                </button>
                        </div>
                </div>
        `,
});

export default defineComponent({
        name: 'PricingPage',
        components: { PriceInput },
        setup() {
                const auth0 = useAuth0();
                const {
                        prices,
                        offerings,
                        currencies,
                        flexGroups,
                        isLoading,
                        error,
                        loadData,
                        getOfferingById,
                        getCurrencyByIso,
                } = usePricingData();
                
                const savingField: Ref<string> = ref('');
                const searchQuery: Ref<string> = ref('');
                const filters: Ref<Filters> = ref({
                        flexGroup: 'all',
                        priceType: 'all',
                        stockStatus: 'instock',
                });
                const currentPage: Ref<number> = ref(1);
                const pageSize: number = 50;
                
                const toastMessage: Ref<string> = ref('');
                const toastVariant: Ref<ToastVariant> = ref('success');
                let toastTimeout: ReturnType<typeof setTimeout> | undefined;

                const showToast = (message: string, variant: ToastVariant = 'success'): void => {
                        if (toastTimeout) clearTimeout(toastTimeout);
                        toastMessage.value = message;
                        toastVariant.value = variant;
                        toastTimeout = setTimeout(() => {
                                toastMessage.value = '';
                        }, 3000);
                };

                const filteredPrices: ComputedRef<PriceRecord[]> = computed(() => {
                        let result = prices.value;
                        
                        if (filters.value.flexGroup !== 'all') {
                                const selectedId = Number(filters.value.flexGroup);
                                const offeringIds = offerings.value
                                        .filter((o: NormalizedOffering) => o.flexgroupid === selectedId)
                                        .map((o: NormalizedOffering) => o.id);
                                result = result.filter((p: PriceRecord) => offeringIds.includes(p.offeringid));
                        }
                        
                        if (filters.value.stockStatus === 'instock') {
                                const inStockOfferingIds = offerings.value
                                        .filter((o: NormalizedOffering) => o.inStock)
                                        .map((o: NormalizedOffering) => o.id);
                                result = result.filter((p: PriceRecord) => inStockOfferingIds.includes(p.offeringid));
                        } else if (filters.value.stockStatus === 'outofstock') {
                                const outOfStockOfferingIds = offerings.value
                                        .filter((o: NormalizedOffering) => !o.inStock)
                                        .map((o: NormalizedOffering) => o.id);
                                result = result.filter((p: PriceRecord) => outOfStockOfferingIds.includes(p.offeringid));
                        }
                        
                        if (filters.value.priceType === 'discounted') {
                                result = result.filter((p: PriceRecord) => 
                                        p.price_base !== p.price_base_from || p.price_page !== p.price_page_from
                                );
                        } else if (filters.value.priceType === 'missing') {
                                result = result.filter((p: PriceRecord) => 
                                        p.price_base === null || p.price_page === null
                                );
                        }
                        
                        if (searchQuery.value.trim()) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter((p: PriceRecord) => {
                                        const offering = getOfferingById(p.offeringid);
                                        return String(offering?.name ?? '').toLowerCase().includes(query) ||
                                                String(p.offeringid).includes(query);
                                });
                        }
                        
                        return result;
                });

                const totalPages: ComputedRef<number> = computed(() => Math.ceil(filteredPrices.value.length / pageSize));

                const paginatedPrices: ComputedRef<PriceRecord[]> = computed(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredPrices.value.slice(start, start + pageSize);
                });

                const visiblePages: ComputedRef<number[]> = computed(() => {
                        const pages: number[] = [];
                        const total = totalPages.value;
                        const current = currentPage.value;

                        let start = Math.max(1, current - 2);
                        let end = Math.min(total, current + 2);

                        if (end - start < 4) {
                                if (start === 1) {
                                        end = Math.min(total, start + 4);
                                } else {
                                        start = Math.max(1, end - 4);
                                }
                        }

                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                const applyFilters = (): void => {
                        currentPage.value = 1;
                };

                const getOfferingName = (id: number): string => {
                        const offering = getOfferingById(id);
                        return offering?.name || `Offering ${id}`;
                };

                const getOfferingDescription = (id: number): string => {
                        const offering = getOfferingById(id);
                        return offering?.description || '';
                };

                const getOfferingThumbnail = (id: number): string | null => {
                        const offering = getOfferingById(id);
                        return offering?.thumbnail || null;
                };

                const getCurrencyDisplay = (code: string): string => {
                        const currency = getCurrencyByIso(code);
                        return currency ? `${currency.name} (${code})` : code;
                };

                const getCurrencyPrefix = (code: string): string => {
                        const currency = getCurrencyByIso(code);
                        return currency?.symbol || code + ' ';
                };

                const handleLoadData = async (): Promise<void> => {
                        try {
                                await loadData();
                        } catch (err: unknown) {
                                const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
                                showToast(errorMessage, 'error');
                        }
                };

                const savePrice = async (priceId: number, field: PriceField, value: number | null): Promise<void> => {
                        savingField.value = `${priceId}-${field}`;
                        try {
                                await PricingService.update(priceId, { [field]: value });
                                const price = prices.value.find((p: PriceRecord) => p.id === priceId);
                                if (price) {
                                        (price as unknown as Record<string, unknown>)[field] = value;
                                }
                                showToast('Price updated');
                        } catch (err: unknown) {
                                const errorMessage = err instanceof Error ? err.message : 'Failed to update price';
                                showToast(errorMessage, 'error');
                        } finally {
                                savingField.value = '';
                        }
                };

                const loggedIn: ComputedRef<boolean> = computed(() => auth0.isAuthenticated.value);

                watch(loggedIn, async (isLoggedIn: boolean) => {
                        if (isLoggedIn) {
                                await handleLoadData();
                        }
                });

                onMounted(async () => {
                        if (auth0.isAuthenticated.value) {
                                await handleLoadData();
                        }
                });

                return {
                        prices,
                        offerings,
                        currencies,
                        flexGroups,
                        isLoading,
                        error,
                        savingField,
                        searchQuery,
                        filters,
                        currentPage,
                        pageSize,
                        totalPages,
                        toastMessage,
                        toastVariant,
                        filteredPrices,
                        paginatedPrices,
                        visiblePages,
                        applyFilters,
                        getOfferingName,
                        getOfferingDescription,
                        getOfferingThumbnail,
                        getCurrencyDisplay,
                        getCurrencyPrefix,
                        savePrice,
                        loadData: handleLoadData,
                };
        },
});
</script>

<style>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
